import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, ArrowRight, ArrowLeft, Loader2, CheckCircle, BarChart3, TrendingUp } from 'lucide-react';
import { aiLiteracyScanApi, AILiteracyQuestion } from '../api/aiLiteracyScan';

type ScanState = 'intro' | 'scan' | 'submitting' | 'results';

export const AILiteracyScan: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<ScanState>('intro');
  const [questions, setQuestions] = useState<AILiteracyQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [attemptId, setAttemptId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const currentQuestion = questions[currentQuestionIndex];
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const data = await aiLiteracyScanApi.getQuestions();
      setQuestions(data);
    } catch (err: any) {
      setError('Kon vragen niet laden. Probeer het later opnieuw.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const startScan = async () => {
    try {
      setLoading(true);
      
      // Generate a temporary user ID for anonymous users
      const tempUserId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const attempt = await aiLiteracyScanApi.createAttempt(tempUserId);
      setAttemptId(attempt.id);
      setState('scan');
    } catch (err: any) {
      setError('Kon scan niet starten. Probeer het later opnieuw.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const selectRating = (rating: number) => {
    setAnswers({
      ...answers,
      [currentQuestion.id]: rating
    });
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const submitScan = async () => {
    try {
      setState('submitting');
      await aiLiteracyScanApi.submitAttempt(attemptId, answers, questions);
      
      // Navigate to results page
      navigate(`/ai-literacy-scan/results/${attemptId}`);
      
    } catch (err: any) {
      setError('Kon resultaten niet opslaan. Probeer het opnieuw.');
      setState('scan');
      console.error(err);
    }
  };

  if (loading && questions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Vragen laden...</p>
        </div>
      </div>
    );
  }

  // Intro Screen
  if (state === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Brain className="w-10 h-10 text-purple-600" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                AI Geletterdheid Zelfscan
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Evalueer je AI-geletterdheid door onderstaande vragen te beantwoorden. 
                Geef voor elke vraag aan hoe bekend je bent met het onderwerp op een schaal van 1 tot 5.
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            <div className="space-y-6 mb-8">
              {/* Score Legend */}
              <div className="bg-gray-50 rounded-2xl p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <BarChart3 className="w-5 h-5 text-purple-600 mr-2" />
                  Betekenis scores
                </h2>
                <div className="grid grid-cols-5 gap-4">
                  {[
                    { score: 1, label: "Helemaal niet bekend/competent" },
                    { score: 2, label: "Enigszins bekend" },
                    { score: 3, label: "Redelijk bekend/competent" },
                    { score: 4, label: "Goed bekend/competent" },
                    { score: 5, label: "Zeer bekend/competent" }
                  ].map(({ score, label }) => (
                    <div key={score} className="text-center">
                      <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold mx-auto mb-2">
                        {score}
                      </div>
                      <p className="text-xs text-gray-600 leading-tight">{label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 rounded-2xl p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Wat kun je verwachten?
                </h2>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>10 vragen</strong> verdeeld over 5 AI-geletterdheid categorieën
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>5 categorieën:</strong> Kennis en Begrip, Toepassing, Herkenning en Kritisch Denken, Ethiek en Beveiliging, Creatie en Ontwikkeling
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>5-10 minuten</strong> om de scan te voltooien
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>Persoonlijk profiel</strong> met radar chart en aanbevelingen
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={startScan}
                disabled={loading}
                className="flex-1 inline-flex items-center justify-center px-8 py-4 bg-purple-600 text-white rounded-2xl font-semibold hover:bg-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Laden...
                  </>
                ) : (
                  <>
                    Start AI Geletterdheid Scan
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-8 py-4 bg-white text-gray-700 border-2 border-gray-200 rounded-2xl font-semibold hover:border-purple-300 hover:bg-purple-50 transition-all duration-300"
              >
                Terug naar Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Scan Screen
  if (state === 'scan' && currentQuestion) {
    const hasAnsweredCurrent = !!answers[currentQuestion.id];
    const allQuestionsAnswered = questions.every(q => !!answers[q.id]);

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Vraag {currentQuestionIndex + 1} van {questions.length}
              </span>
              <span className="text-sm font-medium text-gray-700">
                {Math.round(progress)}% voltooid
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white rounded-3xl shadow-xl p-8 mb-6">
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-4">
                {currentQuestion.category}
              </span>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {currentQuestion.statement}
              </h2>
              
              {currentQuestion.example && (
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <p className="text-gray-700 leading-relaxed">
                    {currentQuestion.example}
                  </p>
                </div>
              )}
            </div>

            {/* Rating Scale */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Hoe bekend ben je met dit onderwerp?
              </h3>
              <div className="grid grid-cols-5 gap-3">
                {[1, 2, 3, 4, 5].map((rating) => {
                  const isSelected = answers[currentQuestion.id] === rating;
                  
                  return (
                    <button
                      key={rating}
                      onClick={() => selectRating(rating)}
                      className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                        isSelected
                          ? 'border-purple-600 bg-purple-50 text-purple-900'
                          : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                      }`}
                    >
                      <div className="text-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2 text-sm font-bold ${
                          isSelected
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {rating}
                        </div>
                        <div className="text-xs text-gray-600">
                          {rating === 1 && 'Helemaal niet'}
                          {rating === 2 && 'Enigszins'}
                          {rating === 3 && 'Redelijk'}
                          {rating === 4 && 'Goed'}
                          {rating === 5 && 'Zeer'}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={goToPreviousQuestion}
              disabled={currentQuestionIndex === 0}
              className="inline-flex items-center px-6 py-3 bg-white text-gray-700 border border-gray-300 rounded-xl font-medium hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Vorige
            </button>

            {currentQuestionIndex === questions.length - 1 ? (
              <button
                onClick={submitScan}
                disabled={!allQuestionsAnswered}
                className="inline-flex items-center px-8 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <TrendingUp className="w-5 h-5 mr-2" />
                {allQuestionsAnswered ? 'Bekijk Je Profiel' : 'Beantwoord alle vragen'}
              </button>
            ) : (
              <button
                onClick={goToNextQuestion}
                disabled={!hasAnsweredCurrent}
                className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Volgende
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Submitting Screen
  if (state === 'submitting') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-xl text-gray-700 font-medium">Je profiel wordt berekend...</p>
        </div>
      </div>
    );
  }

  return null;
};
